<template>
  <div
    class="fixed inset-0 top-80px lt-sm:top-128px bg-white z-90 sm:hidden flex flex-col animate-fade-in-down overflow-hidden"
  >
    <nav class="flex-1 overflow-y-auto py-20px px-24px">
      <div class="flex flex-col gap-8px lt-sm:font-s-30px text-main font-semibold">
        <NuxtLink
          to="/"
          class="mobile-nav-item flex items-center justify-between group"
          @click="$emit('close')"
        >
          <span>首页</span>
        </NuxtLink>

        <!-- Product Center Section -->
        <div class="border-b border-gray-100">
          <div
            class="mobile-nav-item mobile-submenu-trigger"
            :class="{ 'text-primary': isMobileProductsOpen }"
            role="button"
            :aria-expanded="isMobileProductsOpen"
            tabindex="0"
            @click="toggleMobileSubmenu('products')"
            @keydown.enter="toggleMobileSubmenu('products')"
            @keydown.space.prevent="toggleMobileSubmenu('products')"
          >
            <span>产品中心</span>
            <i
              class="i-ep-arrow-right transition-transform duration-300 text-gray-300"
              :class="{
                'rotate-90': isMobileProductsOpen,
                'pf-sc-medium text-primary': isMobileProductsOpen,
              }"
              aria-hidden="true"
            ></i>
          </div>

          <div
            v-show="isMobileProductsOpen"
            class="pl-12px pb-16px flex flex-col gap-12px animate-fade-in"
          >
            <NuxtLink
              v-for="(item, idx) in productItems"
              :key="idx"
              :to="item.link"
              target="_blank"
              class="mobile-product-card"
              @click="$emit('close')"
            >
              <i :class="[item.icon, 'mobile-product-icon']" aria-hidden="true"></i>
              <div class="flex flex-col">
                <div class="lt-sm:font-s-24px pf-sc-semibold text-main">{{ item.title }}</div>
                <div class="lt-sm:font-s-18px text-#64748b mt-2px">{{ item.desc }}</div>
              </div>
            </NuxtLink>
          </div>
        </div>

        <!-- Solutions Section -->
        <div class="border-b border-gray-100">
          <div
            class="mobile-nav-item mobile-submenu-trigger"
            :class="{ 'text-primary': isMobileSolutionsOpen }"
            role="button"
            :aria-expanded="isMobileSolutionsOpen"
            tabindex="0"
            @click="toggleMobileSubmenu('solutions')"
            @keydown.enter="toggleMobileSubmenu('solutions')"
            @keydown.space.prevent="toggleMobileSubmenu('solutions')"
          >
            <span>解决方案</span>
            <i
              class="i-ep-arrow-right transition-transform duration-300 text-gray-300"
              :class="{
                'rotate-90': isMobileSolutionsOpen,
                'text-primary': isMobileSolutionsOpen,
              }"
              aria-hidden="true"
            ></i>
          </div>

          <div
            v-show="isMobileSolutionsOpen"
            class="pl-12px pb-16px flex flex-col gap-24px animate-fade-in"
          >
            <div v-for="(tab, idx) in abilityTabs" :key="idx">
              <div
                class="lt-sm:font-s-26px pf-sc-semibold text-main mb-12px pl-10px flex items-center"
              >
                <span
                  class="lt-sm:w-6px lt-sm:h-28px rounded-full bg-primary mr-8px"
                  aria-hidden="true"
                ></span>
                {{ tab.title }}
              </div>
              <div class="pl-4px grid grid-cols-2 gap-x-10px gap-y-10px">
                <div v-for="(item, i) in abilities[tab.id]" :key="i" class="mobile-solution-card">
                  <NuxtLink
                    :to="`/solutions/${item.id}`"
                    class="flex items-center gap-8px w-full"
                    @click="$emit('close')"
                  >
                    <i :class="[item.icon, 'mobile-solution-icon']" aria-hidden="true"></i>
                    {{ item.title }}
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>
        </div>

        <NuxtLink
          to="/case-studies"
          class="mobile-nav-item flex items-center justify-between group"
          @click="$emit('close')"
        >
          <span>客户案例</span>
        </NuxtLink>
        <NuxtLink
          to="/honor-qualification"
          class="mobile-nav-item flex items-center justify-between group"
          @click="$emit('close')"
        >
          <span>荣誉资质</span>
        </NuxtLink>
        <NuxtLink
          to="/about"
          class="mobile-nav-item flex items-center justify-between group"
          @click="$emit('close')"
        >
          <span>关于绘家</span>
        </NuxtLink>
      </div>
    </nav>
  </div>
</template>

<script setup lang="ts">
  import { productItems } from '~/constants/navigation'
  import { abilities, abilityTabs } from '~/constants/common'

  defineEmits(['close'])

  const isMobileSolutionsOpen = ref(false)
  const isMobileProductsOpen = ref(false)

  const toggleMobileSubmenu = (menu: 'products' | 'solutions') => {
    if (menu === 'products') {
      isMobileProductsOpen.value = !isMobileProductsOpen.value
      if (isMobileProductsOpen.value) isMobileSolutionsOpen.value = false
    } else {
      isMobileSolutionsOpen.value = !isMobileSolutionsOpen.value
      if (isMobileSolutionsOpen.value) isMobileProductsOpen.value = false
    }
  }
</script>

<style scoped lang="scss">
  .mobile-nav-item {
    @apply py-16px border-b border-gray-100 active:text-primary transition-colors;
  }

  .mobile-submenu-trigger {
    @apply flex items-center justify-between !border-none cursor-pointer;
  }

  .mobile-product-card {
    @apply flex items-center gap-12px p-12px rounded-8px bg-gray-50/80 border border-gray-100 transition-all duration-200;
    @apply active:bg-blue-50 active:border-blue-200;
  }

  .mobile-product-icon {
    @apply wh-40px flex-shrink-0;
    @apply lt-sm:wh-78px;
  }

  .mobile-solution-card {
    @apply text-#475569 bg-gray-50/80 border border-gray-100 flex items-center justify-start text-left transition-all duration-200 gap-8px;
    @apply active:bg-blue-50 active:border-blue-200 active:text-blue-600;
    @apply lt-sm:font-s-24px lt-sm:rounded-8px lt-sm:py-18px lt-sm:px-16px;
  }

  .mobile-solution-icon {
    @apply lt-sm:wh-48px flex-shrink-0;
  }

  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in {
    animation: fadeIn 0.3s ease-out forwards;
  }

  .animate-fade-in-down {
    animation: fadeInDown 0.2s ease-out forwards;
  }
</style>
